import { useEffect, useState } from 'react';
import { View } from 'react-native';
import Modal from 'react-native-modal';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { Car } from '@/api/cars/types';
import { useUnlistCar } from '@/api/partners/cars/use-unlist-car';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import mixpanelService from '@/core/clients/mixpanel';
import { Button, Text } from '@/ui';

interface UnlistCarModalProps {
  visible?: boolean;
  onSuccess: () => void;
  onClose: () => void;
  car: Car;
  style?: any;
}

export function UnlistCarModal({
  visible = false,
  onSuccess,
  onClose,
  car,
  style,
}: UnlistCarModalProps) {
  const { styles, theme } = useStyles(stylesheet);
  const [showModal, setShowModal] = useState(visible);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const { mutateAsync: unlistCar, isPending: isUnlistingCar } = useUnlistCar();

  useEffect(() => {
    setShowModal(visible);
  }, [visible]);

  const handleUnlistCar = async () => {
    try {
      await unlistCar({
        id: car.id,
      });

      mixpanelService.track('Car Unlisted', {
        'Car VIN': car.vin,
      });

      setShowModal(false);
      onSuccess();
    } catch (e: any) {
      setErrorMessage(e.message);
    }
  };

  return (
    <Modal
      animationIn="slideInUp"
      animationInTiming={300}
      animationOut="slideOutDown"
      animationOutTiming={300}
      isVisible={showModal}
      hasBackdrop
      style={{
        ...styles.container,
        ...style,
      }}
      onBackdropPress={() => {
        setShowModal(false);
        onClose?.();
      }}
    >
      <View style={styles.containerView}>
        <View
          style={{
            flexDirection: 'row',
            paddingTop: 16,
            paddingLeft: 16,
          }}
        >
          <View>
            <Text type="title2">Unlist Car</Text>
          </View>

          <NavigationButton
            type={NavigationButtonTypes.cancel}
            onPress={() => {
              setShowModal(false);
              onClose?.();
            }}
            style={{
              position: 'absolute',
              right: 16,
              top: 16,
            }}
          />
        </View>

        <View style={{ padding: 16, gap: 24 }}>
          <Text
            type="bodyRegular"
            style={{ color: theme.colors.text.secondary }}
          >
            Are you sure you want to unlist your car? This car will be
            disconnected and not available to book. Relist anytime from the Cars
            page.
          </Text>

          <View style={{ gap: 8 }} />
        </View>

        <View
          style={{
            flexDirection: 'column',
            alignItems: 'flex-end',
            padding: 16,
            paddingTop: 0,
            gap: 24,
          }}
        >
          <View style={styles.buttonsContainer}>
            <Button
              text="Unlist Car"
              type="primary"
              onPress={handleUnlistCar}
              style={[styles.button]}
              loading={isUnlistingCar}
              disabled={isUnlistingCar}
            />

            <Button
              text="Cancel"
              type="ghost"
              onPress={() => {
                setShowModal(false);
                onClose();
              }}
              style={styles.button}
            />
          </View>

          {errorMessage ? (
            <Text type="caption1" style={{ color: theme.colors.error }}>
              Error: {errorMessage}
            </Text>
          ) : null}
        </View>
      </View>
    </Modal>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    margin: 0,
    marginTop: 48,
    paddingBottom: {
      xs: 16,
      md: 0,
    },
    alignSelf: 'center',
    maxWidth: {
      xs: '100%',
      sm: 600,
    },
    width: {
      xs: '100%',
    },
  },
  containerView: {
    backgroundColor: theme.colors.background_2,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    gap: 24,
    borderRadius: 16,
    borderBottomLeftRadius: {
      xs: 0,
      md: 16,
    },
    borderBottomRightRadius: {
      xs: 0,
      md: 16,
    },
  },
  button: {
    width: {
      xs: '100%',
      md: 150,
    },
  },
  buttonsContainer: {
    flexDirection: {
      xs: 'column',
      md: 'row-reverse',
    },
    width: '100%',
    gap: 8,
    justifyContent: {
      xs: 'center',
      md: 'flex-start',
    },
    paddingLeft: 16,
    paddingRight: 16,
  },
}));
